import React, { useState } from "react";
import { Button } from "../components/ui/Button";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";

import InviteTeammate from "../components/InviteTeammate";
import { getApi } from "../lib/get-api";
import { useThemeMode } from "../lib/theme-mode";
import { useAuth } from "../lib/useAuth";
import { Input } from "../components/ui/Input";
import { PasswordInput } from "../components/ui/PasswordInput";

const THEME_OPTIONS: { value: "light" | "dark" | "system"; label: string }[] = [
  { value: "light", label: "Light" },
  { value: "dark", label: "Dark" },
  { value: "system", label: "System" },
];


const Settings: React.FC = () => {
  const { user, guest, token, logout } = useAuth();
  const navigate = useNavigate();
  const { mode, setMode } = useThemeMode();
  const themeId = React.useId();
  const [showCopied, setShowCopied] = useState(false);
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [sendingCode, setSendingCode] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [codeSent, setCodeSent] = useState(false);

  const email = typeof user?.email === "string" ? user.email : "";
  const guestName = typeof guest?.name === "string" ? guest.name.trim() : "";
  const isGuest = Boolean(guest) && !email;
  const guestId = guest?.id || "";

  const copySessionId = async () => {
    if (!guestId) return;
    try {
      await navigator.clipboard.writeText(guestId);
      setShowCopied(true);
      setTimeout(() => setShowCopied(false), 2000);
    } catch {
      toast.error("Could not copy session id");
    }
  };

  const requestReset = async () => {
    if (!email) return;
    setSendingCode(true);
    try {
      const api = getApi({ token });
      await api.auth.authControllerRequestPasswordReset({ email });
      setCodeSent(true);
      toast.success(`Reset code sent to ${email}`);
    } catch (error: any) {
      toast.error(
        error.response?.data?.message || "Failed to send password reset code",
      );
    } finally {
      setSendingCode(false);
    }
  };

  const submitReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    setResetting(true);
    try {
      const api = getApi({ token });
      await api.auth.authControllerResetPassword({
        code: resetCode.trim(),
        newPassword,
      });
      toast.success("Password updated");
      setResetCode("");
      setNewPassword("");
      setConfirmPassword("");
      setCodeSent(false);
    } catch (error: any) {
      toast.error(
        error.response?.data?.message || "Failed to reset password",
      );
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="bg-od-bg text-od-text min-h-full w-full max-w-4xl space-y-6">
      <h1 className="text-2xl font-semibold text-od-text">Settings</h1>

      <section className="bg-od-surface border border-od-border rounded-lg p-4 space-y-3">
        <h2 className="text-sm font-semibold text-od-text">Account</h2>
        {isGuest ? (
          <>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-semibold tracking-wide bg-od-primary/15 text-od-primary-text border border-od-primary/30">
                Guest
              </span>
              {guestName && guestName !== "Guest" && (
                <span className="text-sm text-od-text">{guestName}</span>
              )}
            </div>
            <div className="flex items-center gap-1.5">
              <label
                htmlFor="settings-session-id"
                className="text-xs text-od-muted whitespace-nowrap"
              >
                Session ID
              </label>
              <span
                id="settings-session-id"
                className="bg-od-bg border border-od-border text-od-text px-2 py-1 rounded text-xs flex-1 min-w-0 truncate font-mono"
                title={guestId}
              >
                {guestId}
              </span>
              <Button
                onClick={copySessionId}
                variant="secondary" size="sm"
                title="Copy Guest ID"
              >
                {showCopied ? "Copied" : "Copy"}
              </Button>
            </div>
            <Button
              onClick={() => {
                logout();
                navigate("/auth");
              }}
              variant="link" size="sm" className="self-start text-sm text-od-primary-text hover:text-od-primary-text hover:underline"
            >
              Register to save your work
            </Button>
          </>
        ) : (
          <div className="space-y-1">
            <p className="text-sm text-od-text">{email || "Signed in"}</p>
            {user?.firstName && (
              <p className="text-xs text-od-muted">
                {user.firstName} {user.lastName}
              </p>
            )}
          </div>
        )}
      </section>

      <InviteTeammate />

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
      <section className="bg-od-surface border border-od-border rounded-lg p-4 space-y-3">
        <h2 className="text-sm font-semibold text-od-text">Appearance</h2>
        <div
          role="radiogroup"
          aria-label="Theme"
          className="flex flex-wrap gap-2"
        >
          {THEME_OPTIONS.map((option) => {
            const selected = mode === option.value;
            return (
              <div key={option.value}>
              <input
                id={`${themeId}-${option.value}`}
                name={`${themeId}-theme`}
                type="radio"
                value={option.value}
                checked={selected}
                onChange={() => setMode(option.value)}
                className="peer sr-only"
              />
              <label
                htmlFor={`${themeId}-${option.value}`}
                className={`inline-flex min-h-11 sm:min-h-0 sm:h-8 [@media(pointer:coarse)]:min-h-11 cursor-pointer items-center rounded-md border bg-od-surface px-2.5 text-sm font-medium transition-colors hover:bg-od-surface-2 active:underline peer-focus-visible:ring-2 peer-focus-visible:ring-od-primary-text peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-od-surface ${
                  selected
                    ? "border-od-primary text-od-primary-text"
                    : "border-od-interactive-boundary text-od-muted hover:border-od-border-strong hover:text-od-text"
                }`}
                style={
                  selected
                    ? {
                        background:
                          "color-mix(in srgb, var(--od-primary) 12%, transparent)",
                      }
                    : undefined
                }
              >
                {option.label}
              </label>
              </div>
            );
          })}
        </div>
      </section>

      {!isGuest && (
      <section className="bg-od-surface border border-od-border rounded-lg p-4 space-y-3">
        <h2 className="text-sm font-semibold text-od-text">Password</h2>
        {email ? (
          <div className="space-y-4">
            <p className="text-sm text-od-muted">
              Send a reset code to {email}, then enter it with a new password.
            </p>
            <Button
              onClick={requestReset}
              disabled={sendingCode}
              variant="primary" className="text-sm"
            >
              {sendingCode
                ? "Sending…"
                : codeSent
                  ? "Resend reset code"
                  : "Send reset code"}
            </Button>
            {codeSent && (
            <form onSubmit={submitReset} className="space-y-3">
              <div>
                <label
                  htmlFor="settings-reset-code"
                  className="text-xs font-semibold text-od-text block mb-1.5"
                >
                  Reset code
                </label>
                <Input
                  id="settings-reset-code"
                  type="text"
                  required
                  disabled={resetting}
                  value={resetCode}
                  onChange={(e) => setResetCode(e.target.value)}
                  className="font-mono"
                  autoComplete="one-time-code"
                />
              </div>
              <div>
                <label
                  htmlFor="settings-new-password"
                  className="text-xs font-semibold text-od-text block mb-1.5"
                >
                  New password
                </label>
                <PasswordInput
                  id="settings-new-password"
                  required
                  minLength={8}
                  disabled={resetting}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  autoComplete="new-password"
                  placeholder="At least 8 characters"
                />
              </div>
              <div>
                <label
                  htmlFor="settings-confirm-password"
                  className="text-xs font-semibold text-od-text block mb-1.5"
                >
                  Confirm password
                </label>
                <PasswordInput
                  id="settings-confirm-password"
                  required
                  minLength={8}
                  disabled={resetting}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                  placeholder="Repeat the new password"
                />
              </div>
              <Button
                type="submit"
                disabled={resetting || !resetCode.trim()}
                variant="primary" className="text-sm"
              >
                {resetting ? "Updating…" : "Update password"}
              </Button>
            </form>
            )}
          </div>
        ) : (
          <p className="text-sm text-od-muted">
            Guest sessions have no password.
          </p>
        )}
      </section>
      )}
      </div>
    </div>
  );
};

export default Settings;
